import { Injectable } from '@angular/core';
import { Observable, map, switchMap, tap } from 'rxjs';
import { createStore, select, withProps } from '@ngneat/elf';
import { localStorageStrategy, persistState } from '@ngneat/elf-persist-state';
import { ApiV1Service } from '../api/apiv1.service';
import { Router } from '@angular/router';
import * as localforage from 'localforage';
import { environment } from 'src/environments/environment';

const STORE_NAME = 'TOKEN';
const AUTH_METHOD_KEY = 'authMethod';
const AZURE_SSO_AUTH_METHOD = 'azure-sso';

const authStore = createStore(
  { name: STORE_NAME },
  withProps<IAuthState>({ token: '' }),
);

export const persist = persistState(authStore, {
  key: 'auth',
  storage: localStorageStrategy,
});

@Injectable()
export class AuthCoreService {
  static token$ = authStore.pipe(select((state) => state.token));
  static loginState$ = authStore.pipe(
    select((state) => !!(state.companyUser && state.token)),
  );
  static companyUser$ = authStore.pipe(select((state) => state.companyUser));
  static get isLoggedIn(): boolean {
    return authStore.query((state) => !!(state.companyUser && state.token));
  }
  static get companyUser(): ICompanyUser {
    return authStore.query((state) => state.companyUser!);
  }
  static get token(): string {
    return authStore.query((state) => state.token);
  }
    static setToken(token: string): void {
    authStore.update((state) => ({
      ...state,
      token: token,
    }));
  }
  
  constructor(private _apiV1: ApiV1Service, private _router: Router) {
    AuthCoreService.loginState$.subscribe({
      next: (loginState: boolean) => {
        console.log('loginState: ', loginState);
        if (!loginState) {
          const current = window.location.pathname + window.location.search;
          if (!current.startsWith('/auth')) {
            localStorage.setItem('returnUrl', current);
            this._router.navigateByUrl(this.loginRoute);
          }
        }
      },
    });
  }

  public adminv1Login(
    email: string,
    password: string,
  ): Observable<ICompanyUser> {
    const data = { email, password };
    return this._apiV1.postWithCredentials('/auth/local/', data).pipe(
      tap((data: any) => {
        localStorage.setItem(AUTH_METHOD_KEY, 'password');
        authStore.update(() => {
          return { token: data.token };
        });
      }),
      switchMap(() => this.getUserAndCompanyDetail()),
    );
  }
  // We will move this and related interfaces to its own module then use JWTInterceptor if we are ever going to expand it.
  public getUserAndCompanyDetail(): Observable<ICompanyUser> {
    return this._apiV1
      .get('/api/users/companyLogin/', {
        Authorization: `Bearer ${AuthCoreService.token}`,
      })
      .pipe(
        map((data: any) => data as ICompanyUser),
        tap((data: ICompanyUser) => {
          authStore.update((state: IAuthState) => {
            return { companyUser: data, token: state.token };
          });
        }),
      );
  }

  public logout(): void {
  this._apiV1
      .post('/auth/local/logout', {
        Authorization: `Bearer ${AuthCoreService.token}`,
      })
      .subscribe({
        next: () => {
          this.clearAuthState();
        },
        error: () => {
          this.clearAuthState();
        },
      });
  }

  public forceLogout(): void {
    this.clearAuthState();
  }

  private clearAuthState(): void {
    const current = window.location.pathname + window.location.search;
    if (!current.startsWith('/auth')) {
      localStorage.setItem('returnUrl', current);
    }
    authStore.reset();
    sessionStorage.clear();
    this._router.navigateByUrl(this.loginRoute);
  }

  private get loginRoute(): string {
    return localStorage.getItem(AUTH_METHOD_KEY) === AZURE_SSO_AUTH_METHOD
      ? '/auth/azuresso-login'
      : '/auth/login';
  }
  
  deleteAllData(): void {
    localforage
      .dropInstance({ name: environment.cacheDbName })
      .then(() => {
        console.log(
          `Database ${environment.cacheDbName} deleted successfully!`,
        );
      })
      .catch((err) => {
        console.log('Error in deleting Database: ', err);
      });
  }
}
export interface ICustomVerditBoundary {
  title: string;
  maxValue: number;
  _id: string;
}

export interface ICompany {
  _id: string;
  company_name: string;
  company_logo: string;
  game_name: string;
  facts: {
    fact1: string;
    fact2: string;
    fact3: string;
    fact4: string;
  };
  OrgCompNum: number;
  CampusNum: number;
  JobFitNNum: number;
  JobFitMNum: number;
  customVerditBoundaries: ICustomVerditBoundary[];
  aptitude: string;
  campus: string;
  organisationalMatch: any; // Type should be specified
  customisedManagerial: any; // Type should be specified
  customisedIndividual: string;
  Reports: string;
  Proctoring: string;
  EmailSetting: boolean;
  techAssessments: any[]; // Type should be specified
  aptitudeGames: any[]; // Type should be specified
  sections: string[];
  attempts: string;
  CampusStory: string;
  AssesmentStory: string;
  languages: {
    [key: string]: boolean;
  }[];
  enableTranslation?: boolean;
}

export interface IUser {
  _id: string;
  name: string;
  email: string;
  company_id: string;
  role: string;
}

export interface ICompanyUser {
  user: IUser;
  company: ICompany;
}

export interface IAuthState {
  companyUser?: ICompanyUser;
  token: string;
}

export enum UserRole {
  ADMIN = 'admin',
  COMPANY = 'company',
  USER = 'user',
  EDITOR = 'editor',
}
